import {Injectable} from '@angular/core';
import {ItemService} from '../../common/item/item.service';
import {plainToInstance} from 'class-transformer';
import {ItemFilter, ItemListFilter, ItemQuery, WithSpecsModel} from 'web-shared';
import {map, shareReplay, switchMap} from 'rxjs/operators';
import {FilterModel, getBase64, GLOBAL, LeModalService, MesApiService} from 'common-shared';
import {ManufacturingItemModel} from "web-shared";
import {Observable} from "rxjs";
import {ItemLogModel} from "./models/item-log.model";
import {ItemLogModalComponent} from "./item-log/item-log-modal/item-log-modal.component";

@Injectable({
  providedIn: 'root'
})
export class ManufacturingItemService extends MesApiService {
  constructor(private itemService: ItemService, private leModalService: LeModalService) {
    super('ManufacturingItem');
  }

  getAll$: Observable<ItemQuery[]> = this.itemService.getAll$.pipe(map(value => {
    return value.filter(item => [GLOBAL.Classificators.ItemType.FinishedItem, GLOBAL.Classificators.ItemType.ManufacturedPart, GLOBAL.Classificators.ItemType.IntermediateItem, GLOBAL.Classificators.ItemType.Phantom].includes(item.ItemTypeClv));
  }), shareReplay(1));

  getAllByGroup(groupId: string) {
    return this.getList<ItemQuery>({
      action: 'GetItemsByType',
      area: 'manufacturing',
      controller: 'Item'
    }, {
      id: groupId,
    }).pipe(map(value => {
      return plainToInstance(ItemQuery, value.data);
    }));
  }

  loadOne(id: string): Observable<ManufacturingItemModel> {
    return this.getSingle<ManufacturingItemModel>({
      id: id
    }).pipe(map(value => {
      return plainToInstance(ManufacturingItemModel, value.data);
    }));
  }

  copy(id: string, newCode: string, newName: string, replaceId: string = null) {
    let query = {newCode: newCode, newName: newName};
    if (replaceId) {
      query['targetItemId'] = replaceId;
    }

    return this.getSingle<ManufacturingItemModel>({
      id: id, action: 'Copy'
    }, query).pipe(map(value => {
      return value.data.Id;
    }));
  }

  copyAsDraft(id: string, model: ManufacturingItemModel = null) {
    return this.update<ManufacturingItemModel>(id, model, {
      action: 'CopyAsDraft'
    }).pipe(map(value => {
      return value.data.Id;
    }));
  }

  publish(id: string) {
    return this.getSingle<ManufacturingItemModel>({
      id: id, action: 'Publish'
    }).pipe(map(value => {
      return value.data;
    }));
  }

  addNew(manufacturedItem: ManufacturingItemModel) {
    return this.add<ManufacturingItemModel>(manufacturedItem).pipe(map(value => {
      return plainToInstance(ManufacturingItemModel, value.data);
    }));
  }

  save(manufacturedItem: ManufacturingItemModel) {
    return this.update<ManufacturingItemModel>(manufacturedItem.Id, manufacturedItem, undefined,{clear: true}).pipe(map(value => {
      return plainToInstance(ManufacturingItemModel, value.data);
    }));
  }

  remove(manufacturedItem: ManufacturingItemModel) {
    return this.delete<ManufacturingItemModel>(manufacturedItem.Id).pipe(map(value => {
      return plainToInstance(ManufacturingItemModel, value.data);
    }));
  }

  downloadListExcel(filter: FilterModel<ItemListFilter>, includeImeTab: boolean) {
    return this.http.post(this.getRoute({action: 'ExportListToExcel'}) + '?includeImeTab=' + includeImeTab, filter, {
      observe: 'response',
      responseType: 'blob'
    }).pipe(map(value => {
      return this.getFileNameAndData(value);
    }));
  }

  downloadCompactListExcel(filter: FilterModel<ItemListFilter>) {
    return this.http.post(this.getRoute({action: 'ExportListToCompactExcel'}), filter, {
      observe: 'response',
      responseType: 'blob'
    }).pipe(map(value => {
      return this.getFileNameAndData(value);
    }));
  }

  downloadCostListExcel(filter: FilterModel<ItemListFilter>) {
    return this.http.post(this.getRoute({action: 'ExportListToCostExcel'}), filter, {
      observe: 'response',
      responseType: 'blob'
    }).pipe(map(value => {
      return this.getFileNameAndData(value);
    }));
  }

  uploadExcel(file: File): Observable<number> {
    return getBase64(file).pipe(switchMap(fileData => {
      return this.addGet<string, number>(
        fileData,
        { action: 'ImportFromExcel' }
      ).pipe(map(value => {
        return value.data;
      }));
    }));
  }

  getIdsByFilter(value: ItemFilter): Observable<string[]> {
    return this.addGet<ItemFilter, string[]>(value, {action: 'GetIdsByFilter'}).pipe(map(value => value.data));
  }

  resetModifiedBy(id: string) {
    return this.getSingle<ManufacturingItemModel>({action: 'ResetModifiedBy'}, {id: id}).pipe(map(value => {
      return plainToInstance(ManufacturingItemModel, value.data);
    }));
  }

  getLogs(id: string, context: string[], statusId: string = null): Observable<ItemLogModel[]> {
    return this.getList<ItemLogModel>({
      action: 'GetLogs'
    }, {id: id, context: context, statusId: statusId}).pipe(map(value => {
      return plainToInstance(ItemLogModel, value.data);
    }));
  }

  getLastLog(itemId: string, statusId: string): Observable<ItemLogModel> {
    return this.getLogs(itemId, null, statusId).pipe(map(value => value.pop()));
  }

  openLogsModal(id: string, context: string[]) {
    return this.leModalService.show(ItemLogModalComponent, {initialState: {id: id, context: context}, class: 'modal-full'}).onHide$;
  }

  addLog(log: ItemLogModel): Observable<ItemLogModel> {
    return this.add<ItemLogModel>(log, {action: 'AddLog'}).pipe(map(value => {
      return plainToInstance(ItemLogModel, value.data);
    }));
  }

  saveLog(log: ItemLogModel): Observable<ItemLogModel> {
    return this.update<ItemLogModel>(log.Id, log, {action: 'EditLog'}).pipe(map(value => {
      return plainToInstance(ItemLogModel, value.data);
    }));
  }

  removeLog(log: ItemLogModel): Observable<ItemLogModel> {
    return this.delete<ItemLogModel>(log.Id, {action: 'DeleteLog'}).pipe(map(value => {
      return plainToInstance(ItemLogModel, value.data);
    }));
  }

  getStatusClassificator(itemId: string, itemGroupId: string = null){
    return this.getSingle<string>({action: 'GetStatusClassificator'}, {itemId: itemId, itemGroupId: itemGroupId}).pipe(map(value => {
      return value.data;
    }));
  }

  ensureLowLevelCodes(){
    return this.getSingle<boolean>({action: 'EnsureLowLevelCodes'}).pipe(map(value => {
      return value.data;
    }));
  }

  getWithSpecs(ids: string[], propertyIds: string[]) {
    return this.itemService.getWithSpecs(ids, propertyIds);
  }
}
